import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const SearchFilters = ({ onSearch, onFilter, filters }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('filename');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    documentType: '',
    dateRange: '',
    tags: '',
    minSize: '',
    maxSize: ''
  });

  const documentTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'pdf', label: 'PDF Documents' },
    { value: 'image', label: 'Images' },
    { value: 'spreadsheet', label: 'Spreadsheets' },
    { value: 'document', label: 'Word Documents' },
    { value: 'other', label: 'Other Files' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'Any Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const searchTypeOptions = [
    { value: 'filename', label: 'File Name' },
    { value: 'content', label: 'Content (OCR)' },
    { value: 'tags', label: 'Tags' },
    { value: 'all', label: 'All Fields' }
  ];

  const handleSearch = (e) => {
    e?.preventDefault();
    onSearch({
      query: searchQuery,
      type: searchType,
      ...advancedFilters
    });
  };

  const handleAdvancedFilterChange = (key, value) => {
    const newFilters = { ...advancedFilters, [key]: value };
    setAdvancedFilters(newFilters);
    onFilter(newFilters);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSearchType('filename');
    setAdvancedFilters({
      documentType: '',
      dateRange: '',
      tags: '',
      minSize: '',
      maxSize: ''
    });
    onSearch({ query: '', type: 'filename' });
    onFilter({});
  };

  return (
    <div className="bg-surface border-b border-border">
      {/* Main Search Bar */}
      <div className="p-4">
        <form onSubmit={handleSearch} className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
            />
            <Input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
          
          <Select
            options={searchTypeOptions}
            value={searchType}
            onChange={setSearchType}
            className="w-40"
          />
          
          <Button type="submit" iconName="Search">
            Search
          </Button>
          
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? 'ChevronUp' : 'ChevronDown'}
          >
            Filters
          </Button>
        </form>
      </div>
      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="px-4 pb-4 border-t border-border bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Select
              label="Document Type"
              options={documentTypeOptions}
              value={advancedFilters?.documentType}
              onChange={(value) => handleAdvancedFilterChange('documentType', value)}
            />
            
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={advancedFilters?.dateRange}
              onChange={(value) => handleAdvancedFilterChange('dateRange', value)}
            />
            
            <Input
              label="Tags"
              type="text"
              placeholder="Enter tags..."
              value={advancedFilters?.tags}
              onChange={(e) => handleAdvancedFilterChange('tags', e?.target?.value)}
            />
            
            <div className="flex space-x-2">
              <Input
                label="Min Size (MB)"
                type="number"
                placeholder="0"
                value={advancedFilters?.minSize}
                onChange={(e) => handleAdvancedFilterChange('minSize', e?.target?.value)}
                className="flex-1"
              />
              <Input
                label="Max Size (MB)"
                type="number"
                placeholder="100"
                value={advancedFilters?.maxSize}
                onChange={(e) => handleAdvancedFilterChange('maxSize', e?.target?.value)}
                className="flex-1"
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={clearFilters}>
              Clear All Filters
            </Button>
          </div>
        </div>
      )}
      {/* Active Filters Display */}
      {(searchQuery || Object.values(advancedFilters)?.some(v => v)) && (
        <div className="px-4 py-2 bg-primary/5 border-t border-border">
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-muted-foreground">Active filters:</span>
            
            {searchQuery && (
              <span className="bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center space-x-1">
                <span>"{searchQuery}"</span>
                <button onClick={() => setSearchQuery('')}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {advancedFilters?.documentType && (
              <span className="bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center space-x-1">
                <span>Type: {documentTypeOptions?.find(opt => opt?.value === advancedFilters?.documentType)?.label}</span>
                <button onClick={() => handleAdvancedFilterChange('documentType', '')}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            {advancedFilters?.dateRange && (
              <span className="bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center space-x-1">
                <span>Date: {dateRangeOptions?.find(opt => opt?.value === advancedFilters?.dateRange)?.label}</span>
                <button onClick={() => handleAdvancedFilterChange('dateRange', '')}>
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            
            <button
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground ml-2"
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchFilters;